import type { components } from '@pba/types-openapi-node';
import { parseISO } from 'date-fns';
import { isProgramEventInProgress } from './program-event';
import type { UploadedFile } from './uploaded-file';

export type Event =
  | EventCall
  | EventClinicalReview
  | EventReminder
  | EventTelehealthVisit
  | EventVideoConference
  | EventWebinar;

export type EventCall = {
  channel?: never;
  distributionList?: never;
  latestConfirmedParticipants?: never;
  preloadedFiles?: UploadedFile[];
  recording?: never;
  type: 'call';
} & BaseEvent &
  EventWithMedicalGroup;

export type EventClinicalReview = {
  channel?: never;
  distributionList?: never;
  latestConfirmedParticipants?: never;
  preloadedFiles?: UploadedFile[];
  recording?: never;
  type: 'clinical_review';
} & BaseEvent &
  EventWithMedicalGroup;

export type EventDialInInformation = {
  id?: string;
  number?: string;
  passcode?: string;
  presenterId?: string;
  presenterNumber?: string;
  presenterPasscode?: string;
};

export type EventEmailParticipant = {
  email: string;
  feedback?: EventParticipantFeedback;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  ref?: never;
  role: EventParticipantRole;
  rsvp: EventParticipantRsvp;
  type: 'email';
};

export type EventParticipant = EventEmailParticipant | EventRegisteredParticipant;

export type EventParticipantFeedback = {
  providerRating?: number;
  providerText?: string;
  serviceRating?: number;
  serviceText?: string;
  videoRating?: number;
  videoText?: string;
};

export type EventRegisteredParticipant = {
  email?: string;
  feedback?: EventParticipantFeedback;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  ref: string;
  role: EventParticipantRole;
  rsvp: EventParticipantRsvp;
  type: 'user';
};

export type EventReminder = {
  allDay?: boolean;
  channel?: never;
  coverPhoto?: string;
  distributionList?: EventWithDistributionList[];
  latestConfirmedParticipants?: never;
  preloadedFiles?: UploadedFile[];
  recording?: never;
  type: 'reminder';
} & BaseEvent &
  EventWithoutMedicalGroup;

export type EventTelehealthVisit = {
  channel?: never;
  distributionList?: never;
  latestConfirmedParticipants?: never;
  preloadedFiles?: UploadedFile[];
  recording?: never;
  type: 'telehealth_visit';
} & BaseEvent &
  EventWithMedicalGroup;

export type NonPendingEventRsvpStatus = Exclude<EventParticipantRsvp, 'pending'>;

type EventWithMedicalGroup = {
  medicalGroup: string;
  medicalGroupName: string;
};

type EventWithoutMedicalGroup = {
  medicalGroup?: never;
  medicalGroupName?: never;
};

const createEventParticipantFeedback = (data: components['schemas']['Feedback']): EventParticipantFeedback => ({
  providerRating: data.providerRating,
  providerText: data.providerText,
  serviceRating: data.serviceRating,
  serviceText: data.serviceText,
  videoRating: data.videoRating,
  videoText: data.videoText,
});

export const createEventParticipant = (data: components['schemas']['EventParticipant']): EventParticipant => {
  const participant = {
    email: data.email,
    feedback: data.feedback ? createEventParticipantFeedback(data.feedback) : undefined,
    firstName: data.firstName,
    lastName: data.lastName,
    middleName: data.middleName,
    role: data.role,
    rsvp: data.rsvp,
  };

  switch (data.type) {
    case 'email':
      return {
        ...participant,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- if participant is type 'email', email is required
        email: data.email!,
        type: 'email',
      };

    case 'user':
      return {
        ...participant,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- if participant is type 'user', ref is required
        ref: data.ref!,
        type: 'user',
      };
  }
};

export type BaseEvent = {
  author?: string;
  coverPhoto?: string;
  dialInInformation?: EventDialInInformation;
  duration?: number;
  endTime: Date;
  goingCount: number;
  html?: string;
  id: string;
  isBookmarked?: boolean;
  name: string;
  notGoingCount: number;
  participants: EventParticipant[];
  pendingCount: number;
  startTime: Date;
  status: EventStatus;
  text?: string;
};

export type EventParticipantRole = 'attendee' | 'moderator' | 'presenter';

export type EventParticipantRsvp = 'going' | 'not_going' | 'pending';

export type EventRecordingStatus = 'not_available' | 'processing' | 'recorded';

export type EventStatus = 'active' | 'canceled' | 'draft';

export type EventType = 'call' | 'clinical_review' | 'reminder' | 'telehealth_visit' | 'video_conference' | 'webinar';

export type EventVideoConference = {
  channel?: Channel;
  distributionList?: EventWithDistributionList[];
  latestConfirmedParticipants?: string[];
  preloadedFiles?: UploadedFile[];
  recording?: Recording;
  type: 'video_conference';
} & BaseEvent &
  EventWithoutMedicalGroup;

export type EventWebinar = {
  channel?: Channel;
  distributionList?: EventWithDistributionList[];
  latestConfirmedParticipants?: string[];
  preloadedFiles?: UploadedFile[];
  recording?: Recording;
  type: 'webinar';
} & BaseEvent &
  EventWithoutMedicalGroup;

export type EventWithDistributionList = {
  communityId: string;
  communityName: string;
};

export type Recording = {
  status?: EventRecordingStatus;
  url?: string;
};

type Channel = {
  featuredInChannels?: boolean;
  organization?: string;
  organizationHashId?: string;
  showInChannel?: boolean;
  tags?: string[];
};

export const createEventFile = (uploadedFile: components['schemas']['Attachment']): UploadedFile => ({
  byteSize: uploadedFile.size,
  contentType: uploadedFile.mimeType,
  downloadUrl: uploadedFile.url,
  filename: uploadedFile.name,
  id: uploadedFile.ref,
  url: uploadedFile.url,
});

export const createBaseEvent = (data: components['schemas']['PaginatedEvent']['data'][number]): BaseEvent => ({
  ...data,
  endTime: parseISO(data.endTime),
  goingCount: data.goingCount ?? 0,
  id: data._id,
  isBookmarked: data.isBookmarked,
  notGoingCount: data.notGoingCount ?? 0,
  participants: data.participants?.map((participant) => createEventParticipant(participant)) ?? [],
  pendingCount: data.pendingCount ?? 0,
  startTime: parseISO(data.startTime),
  // TODO: handle deleted event
  status: data.status === 'deleted' ? 'canceled' : data.status,
});

export const createEvent = (data: components['schemas']['PaginatedEvent']['data'][number]): Event => {
  switch (data.type) {
    case 'call':
      return createEventCall(data);
    case 'clinical_review':
      return createEventClinicalReview(data);
    case 'reminder':
      return createEventReminder(data);
    case 'telehealth_visit':
      return createEventTelehealthVisit(data);
    case 'video_conference':
      return createEventVideoConference(data);
    case 'webinar':
      return createEventWebinar(data);
  }
};

export const createEventReminder = (data: components['schemas']['ReminderEvent']): EventReminder => ({
  ...createBaseEvent(data),
  allDay: data.allDay,
  coverPhoto: data.images?.coverPhoto,
  distributionList: data.distributionList,
  preloadedFiles: data.preloadedFiles?.map((file) => createEventFile(file)),
  type: 'reminder',
});

const createEventWebinar = (data: components['schemas']['WebinarEvent']): EventWebinar => ({
  ...createBaseEvent(data),
  channel: data.channel,
  coverPhoto: data.images?.coverPhoto,
  distributionList: data.distributionList,
  latestConfirmedParticipants: data.latestConfirmedParticipants,
  preloadedFiles: data.preloadedFiles?.map((file) => createEventFile(file)),
  recording: data.recording,
  type: 'webinar',
});

const createEventCall = (data: components['schemas']['CallEvent']): EventCall => ({
  ...createBaseEvent(data),
  medicalGroup: data.medicalGroup,
  medicalGroupName: data.medicalGroupName,
  type: 'call',
});

const createEventClinicalReview = (data: components['schemas']['ClinicalReviewEvent']): EventClinicalReview => ({
  ...createBaseEvent(data),
  medicalGroup: data.medicalGroup,
  medicalGroupName: data.medicalGroupName,
  type: 'clinical_review',
});

const createEventTelehealthVisit = (data: components['schemas']['TelehealthVisitEvent']): EventTelehealthVisit => ({
  ...createBaseEvent(data),
  medicalGroup: data.medicalGroup,
  medicalGroupName: data.medicalGroupName,
  type: 'telehealth_visit',
});

const createEventVideoConference = (data: components['schemas']['VideoConferenceEvent']): EventVideoConference => ({
  ...createBaseEvent(data),
  channel: data.channel,
  coverPhoto: data.images?.coverPhoto,
  distributionList: data.distributionList,
  latestConfirmedParticipants: data.latestConfirmedParticipants,
  preloadedFiles: data.preloadedFiles?.map((file) => createEventFile(file)),
  recording: data.recording,
  type: 'video_conference',
});

export const getCurrentUserRole = ({
  participants,
  viewerId,
}: {
  participants: Array<{ ref?: string; role: EventParticipantRole }>;
  viewerId: string;
}) => participants.find((participant) => participant.ref === viewerId)?.role;

export const getCurrentUserRsvp = ({
  participants,
  viewerId,
}: {
  participants?: Array<{ ref?: string; rsvp: EventParticipantRsvp }>;
  viewerId: string;
}) => participants?.find((participant) => participant.ref === viewerId)?.rsvp;

export type Translations = {
  en: string;
  es: string;
};

export const createEventJoin = (joinEvent: components['schemas']['EventRegistration']): EventJoin => ({
  enterUri: joinEvent.enterUri,
});

export type EventJoin = {
  enterUri: string;
};

export const isEventInProgress = ({
  endDifferenceInMinutes = 58,
  endTime,
  startDifferenceInMinutes = 0,
  startTime,
}: {
  endDifferenceInMinutes?: number;
  endTime: Date | string;
  startDifferenceInMinutes?: number;
  startTime: Date | string;
}) =>
  isProgramEventInProgress({
    endDifferenceInMinutes,
    endTime,
    startDifferenceInMinutes,
    startTime,
  });

export const isRegisteredEventParticipant = (value?: EventParticipant): value is EventRegisteredParticipant =>
  value?.type === 'user';
